// useTeamViewData: Data loading hook for TeamView
// Manages sites, services, profiles, tasks state and their loading logic
// Also manages date selection, calendar visibility, and scroll trigger state
// Extracted from TeamView.tsx to reduce component complexity
// CHANGE: Removed all debug console statements for production
// FIX: Added 'admin' and 'director' to allowedRoles so admin tasks appear in team planning

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getTeamSites,
  getTeamTasks,
  SiteInfo,
  AssignedTaskWithDetails,
} from '../lib/execution';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/dateUtils';
import { getServices, type Service } from '../lib/services';
import { withSpan } from '../lib/sentry';

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
  role: 'operator' | 'manager' | 'director' | 'admin';
}

interface UseTeamViewDataParams {
  userId: string | null;
  userRole: string | null;
}

export function useTeamViewData({ userId, userRole }: UseTeamViewDataParams) {
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [allProfilesForSite, setAllProfilesForSite] = useState<Profile[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(() => formatDate(new Date()));
  const [allTasksForSite, setAllTasksForSite] = useState<AssignedTaskWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [scrollTriggerKey, setScrollTriggerKey] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);

  const dateObject = useMemo(() => new Date(selectedDate + 'T00:00:00'), [selectedDate]);

  const triggerScroll = useCallback(() => setScrollTriggerKey(prev => prev + 1), []);

  const loadServices = async () => {
    const result = await getServices(false);
    if (result.success && result.data) {
      setServices(result.data);
    }
  };

  const loadSites = async () => {
    if (!userId || !userRole) return;
    try {
      if (sites.length === 0) setLoading(true);
      setError(null);
      const teamSites = await getTeamSites(userId, userRole);
      setSites(teamSites);
    } catch (err) {
      setError('Erreur lors du chargement des sites');
    } finally {
      if (sites.length === 0) setLoading(false);
    }
  };

  const loadProfiles = async (siteId: string) => {
    try {
      setError(null);

      const { data: userSiteData, error: userSiteError } = await supabase
        .from('user_sites')
        .select('user_id')
        .eq('site_id', siteId);

      if (userSiteError) {
        throw userSiteError;
      }

      if (!userSiteData || userSiteData.length === 0) {
        setAllProfilesForSite([]);
        return;
      }

      const userIds = userSiteData.map(us => us.user_id);

      const allowedRoles = ['operator', 'manager', 'director', 'admin'];
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, role')
        .in('id', userIds)
        .in('role', allowedRoles);

      if (usersError) {
        throw usersError;
      }

      const teamProfiles: Profile[] = usersData?.map((user: any) => ({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        role: user.role,
      })) || [];

      const sortedProfiles = teamProfiles.sort((a, b) => {
        const rolePriority: Record<string, number> = { manager: 1, director: 2, admin: 3, operator: 4 };
        const priorityA = rolePriority[a.role] ?? 5;
        const priorityB = rolePriority[b.role] ?? 5;
        if (priorityA !== priorityB) return priorityA - priorityB;
        const lastNameCompare = a.last_name.localeCompare(b.last_name);
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.first_name.localeCompare(b.first_name);
      });

      setAllProfilesForSite(sortedProfiles);
    } catch (err) {
      setError('Erreur lors du chargement des membres de l\'équipe');
      setAllProfilesForSite([]);
    }
  };

  const loadTasks = async (siteId: string, isManualRefresh = false) => {
    if (!userId || !userRole) return;
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const allTasks = await withSpan('team.loadTasks', 'db.query', () =>
        getTeamTasks(userId, userRole, siteId, selectedDate)
      );
      setAllTasksForSite(allTasks);
    } catch (err) {
      setError('Erreur lors du chargement des missions');
    } finally {
      if (isManualRefresh) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const clearTasks = () => setAllTasksForSite([]);

  const handleDateChange = (date: Date) => {
    setSelectedDate(formatDate(date));
    triggerScroll();
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setShowCalendar(false);
    triggerScroll();
  };

  const goToToday = () => {
    setSelectedDate(formatDate(new Date()));
    setShowCalendar(false);
    triggerScroll();
  };

  const toggleCalendar = () => setShowCalendar(prev => !prev);

  useEffect(() => {
    loadSites();
    loadServices();
  }, [userId]);

  return {
    sites,
    services,
    allProfilesForSite,
    allTasksForSite,
    selectedDate,
    loading,
    error,
    isRefreshing,
    scrollTriggerKey,
    showCalendar,
    dateObject,
    loadSites,
    loadServices,
    loadProfiles,
    loadTasks,
    clearTasks,
    handleDateChange,
    handleDateSelect,
    goToToday,
    toggleCalendar,
    triggerScroll,
  };
}
